import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { isStaff, isAdmin, describeRoles } from '../types/auth';
import {
  BanknotesIcon,
  ChevronRightIcon,
  ClockIcon,
  HeartIcon,
  InboxArrowDownIcon,
} from '@heroicons/react/24/outline';
import api, { absencesAPI, bookingsAPI, playerApplicationsAPI } from '../services/api';
import type { PlayerAbsence } from '../types/player';
import { absenceBadgeClass, describeAbsence, toDateInputValue } from '../utils/absence';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import DashboardEvents from '../components/dashboard/DashboardEvents';
import Modal from '../components/ui/Modal';
import BoardHighlights from '../components/board/BoardHighlights';
import { useSportDoctorWarningDays } from '../utils/playerMedical';
import WhatsNewBanner from '../components/help/WhatsNewBanner';
import InstallAppBanner from '../components/ui/InstallAppBanner';
import { clubLogoSrc, handleClubLogoError } from '../utils/clubLogo';

interface SportDoctorPlayer {
  playerId: number;
  name: string;
  ageGroup: string;
  sportDoctorValidity: string;
}

interface DashboardData {
  playersCount: number;
  upcomingEventsCount: number;
  outstandingFeesAmount?: number;
  outstandingFeesCount?: number;
  expiringSportDoctorCount?: number;
  expiringSportDoctorPlayers?: SportDoctorPlayer[];
  expiredSportDoctorCount?: number;
  expiredSportDoctorPlayers?: SportDoctorPlayer[];
  todayEvent?: {
    id: number;
    title: string;
    ageGroup?: string;
    startTime: string;
    endTime: string;
    location?: string;
  };
  children?: Array<{
    playerId: number;
    name: string;
    ageGroup: string;
    sportDoctorValidity?: string;
    hasUnpaidFees: boolean;
    unpaidAmount: number;
  }>;
}

const formatAmount = (amount: number) => `${amount.toLocaleString('hu-HU')} Ft`;
const formatDate = (dt: string) => new Date(dt).toLocaleDateString('hu-HU');

/** Hány nap múlva jár le - naptári napokban, az időpont részét elhagyva. */
const daysUntil = (dt: string): number => {
  const target = new Date(dt);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

const describeExpiry = (dt: string): string => {
  const days = daysUntil(dt);
  if (days < 0) return `${-days} napja lejárt`;
  if (days === 0) return 'ma jár le';
  if (days === 1) return 'holnap jár le';
  return `${days} nap múlva`;
};

const Dashboard: React.FC = () => {
  const sportDoctorWarningDays = useSportDoctorWarningDays();
  const { user } = useAuth();
  const { settings: clubSettings } = useClubSettings();
  usePageTitle('Áttekintő');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sportDoctorModal, setSportDoctorModal] = useState<'expiring' | 'expired' | null>(null);
  // Elbírálásra váró vendég foglalások - csak az adminnak van dolga velük
  const [pendingBookings, setPendingBookings] = useState(0);
  // A stábnak: ki van most kidőlve, és hány új jelentkező vár
  const [absences, setAbsences] = useState<PlayerAbsence[]>([]);
  const [absenceModalOpen, setAbsenceModalOpen] = useState(false);
  const [pendingApplications, setPendingApplications] = useState(0);

  useEffect(() => {
    if (!isStaff(user)) return;

    let cancelled = false;
    const today = toDateInputValue(new Date());
    absencesAPI
      .getCurrent()
      // A jövőben kezdődőket is visszaadja - itt csak a ma érvényesek kellenek
      .then((list) => {
        if (!cancelled) setAbsences(list.filter((a) => toDateInputValue(a.startDate) <= today));
      })
      .catch((err) => console.error('A hiányzások betöltése nem sikerült:', err));
    // A jelentkezések az adminra tartoznak
    if (isAdmin(user)) {
      playerApplicationsAPI
        .getPendingCount()
        .then((count) => {
          if (!cancelled) setPendingApplications(count);
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/dashboard');
        setData(res.data);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!isAdmin(user)) {
      setPendingBookings(0);
      return;
    }

    let cancelled = false;
    bookingsAPI
      .getPending()
      .then((list) => {
        if (!cancelled) setPendingBookings(list.length);
      })
      .catch((err) => console.error('A függő foglalások betöltése nem sikerült:', err));

    return () => {
      cancelled = true;
    };
  }, [user]);

  const sportDoctorPlayers =
    sportDoctorModal === 'expired' ? data?.expiredSportDoctorPlayers : data?.expiringSportDoctorPlayers;

  const getWelcomeMessage = () => {
    // Egyszerre több szerep is igaz lehet (pl. Edző ÉS Játékos) - mindet
    // felsoroljuk, nem csak az elsőt egy kizáró láncból.
    return `Üdvözöljük, ${user?.name}! (${describeRoles(user)})`;
  };

  // Teendők: csak ami tényleg dolgot ad, egy tömör listában
  const todos: TodoItem[] = [];
  if (data && isStaff(user)) {
    if ((data.outstandingFeesAmount ?? 0) > 0) {
      todos.push({
        key: 'fees',
        icon: BanknotesIcon,
        tone: 'red',
        title: 'Kintlévőség',
        subtitle: `${data.outstandingFeesCount} fizetetlen tagdíj`,
        value: formatAmount(data.outstandingFeesAmount!),
        to: '/membership-fees',
      });
    }
    if ((data.expiredSportDoctorCount ?? 0) > 0) {
      todos.push({
        key: 'expired',
        icon: HeartIcon,
        tone: 'red',
        title: 'Lejárt sportorvosi',
        subtitle: 'Nem léphetnek pályára',
        value: `${data.expiredSportDoctorCount} fő`,
        onClick: () => setSportDoctorModal('expired'),
      });
    }
    if ((data.expiringSportDoctorCount ?? 0) > 0) {
      todos.push({
        key: 'expiring',
        icon: HeartIcon,
        tone: 'amber',
        title: 'Lejáró sportorvosi',
        subtitle: `A következő ${sportDoctorWarningDays} napban`,
        value: `${data.expiringSportDoctorCount} fő`,
        onClick: () => setSportDoctorModal('expiring'),
      });
    }
    if (absences.length > 0) {
      todos.push({
        key: 'absences',
        icon: HeartIcon,
        tone: 'rose',
        title: 'Beteg / sérült',
        subtitle: 'Bejelentve',
        value: `${absences.length} fő`,
        onClick: () => setAbsenceModalOpen(true),
      });
    }
    if (pendingApplications > 0) {
      todos.push({
        key: 'applications',
        icon: InboxArrowDownIcon,
        tone: 'emerald',
        title: 'Új jelentkező',
        subtitle: 'Elbírálásra vár',
        value: `${pendingApplications} fő`,
        to: '/applications',
      });
    }
    if (pendingBookings > 0) {
      todos.push({
        key: 'bookings',
        icon: ClockIcon,
        tone: 'amber',
        title: 'Foglalás jóváhagyásra vár',
        subtitle: 'Vendégek beküldött foglalásai',
        value: `${pendingBookings} db`,
        to: '/bookings',
      });
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <img
          src={clubLogoSrc(clubSettings?.crestUrl)}
          alt=""
          className="h-12 w-12 object-contain"
          onError={handleClubLogoError}
        />
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Áttekintő</h1>
          <p className="hidden sm:block mt-0.5 truncate text-sm text-gray-600">{getWelcomeMessage()}</p>
        </div>
      </div>

      <WhatsNewBanner />

      {/* Telefonon, ha még nincs a kezdőképernyőn: letöltés */}
      <InstallAppBanner className="mb-6" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Teendők - mobilon elöl, széles képernyőn jobb oldalt */}
        <aside className="space-y-6 lg:order-2">
          {!loading && todos.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg font-semibold text-gray-900">Teendők</h2>
              <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
                {todos.map((todo) => (
                  <li key={todo.key}>
                    <TodoRow item={todo} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Üzenőfal kivonat: a legutóbbi bejegyzések */}
          <BoardHighlights />
        </aside>

        <div className="lg:order-1 lg:col-span-2">
          <DashboardEvents />
        </div>
      </div>

      {/* Kik azok: a widget számához tartozó névsor, hogy ne a teljes
          játékoslistából kelljen kikeresni az érintetteket. */}
      <Modal
        isOpen={sportDoctorModal !== null}
        onClose={() => setSportDoctorModal(null)}
        title={sportDoctorModal === 'expired' ? 'Lejárt sportorvosi' : 'Lejáró sportorvosi'}
      >
        <p className="mb-3 text-sm text-gray-500">
          {sportDoctorModal === 'expired'
            ? 'Akiknek a sportorvosi engedélye már lejárt - a legrégebben lejárt elöl.'
            : `A következő ${sportDoctorWarningDays} napban lejáró sportorvosi engedélyek.`}
        </p>

        {(sportDoctorPlayers?.length ?? 0) === 0 ? (
          // Akkor fordulhat elő, ha a backend még a névsor nélküli, régebbi
          // választ adja - a szám ilyenkor is stimmel, csak a lista hiányzik.
          <p className="py-4 text-sm text-gray-500">A névsor most nem érhető el.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {sportDoctorPlayers!.map((player) => (
              <li key={player.playerId} className="flex items-start justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 break-words hyphens-auto leading-snug">
                    {player.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{player.ageGroup || '-'}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div
                    className={`whitespace-nowrap text-sm font-medium ${
                      sportDoctorModal === 'expired' ? 'text-red-700' : 'text-amber-700'
                    }`}
                  >
                    {formatDate(player.sportDoctorValidity)}
                  </div>
                  <div className="whitespace-nowrap text-xs text-gray-500">
                    {describeExpiry(player.sportDoctorValidity)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-gray-100 pt-3">
          <Link
            to="/players"
            onClick={() => setSportDoctorModal(null)}
            className="text-sm font-medium text-primary-600 hover:underline"
          >
            Játékosok megnyitása
          </Link>
        </div>
      </Modal>

      <Modal isOpen={absenceModalOpen} onClose={() => setAbsenceModalOpen(false)} title="Beteg / sérült játékosok">
        <ul className="divide-y divide-gray-100 pb-4">
          {absences.map((a) => (
            <li key={a.id} className="py-2">
              <Link to={`/players/${a.playerId}`} className="flex items-center justify-between gap-2 hover:underline">
                <span>
                  <span className="font-medium text-gray-900">{a.playerName}</span>
                  <span className="text-xs text-gray-500"> · {a.ageGroup}</span>
                </span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${absenceBadgeClass(a.type)}`}>
                  {describeAbsence(a)}
                </span>
              </Link>
              {a.note && <p className="mt-0.5 text-xs text-gray-500">{a.note}</p>}
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};

interface TodoItem {
  key: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tone: 'red' | 'amber' | 'rose' | 'emerald';
  title: string;
  subtitle: string;
  value: string;
  to?: string;
  onClick?: () => void;
}

const toneClasses: Record<TodoItem['tone'], { icon: string; value: string }> = {
  red: { icon: 'bg-red-100 text-red-600', value: 'text-red-700' },
  amber: { icon: 'bg-amber-100 text-amber-600', value: 'text-amber-700' },
  rose: { icon: 'bg-rose-100 text-rose-600', value: 'text-rose-700' },
  emerald: { icon: 'bg-emerald-100 text-emerald-600', value: 'text-emerald-700' },
};

/** Egy teendő sor: link egy oldalra, vagy a névsort mutató ablak. */
const TodoRow: React.FC<{ item: TodoItem }> = ({ item }) => {
  const Icon = item.icon;
  const tone = toneClasses[item.tone];
  const body = (
    <>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone.icon}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{item.title}</span>
        <span className="block truncate text-xs text-gray-500">{item.subtitle}</span>
      </span>
      <span className={`shrink-0 text-sm font-semibold tabular-nums ${tone.value}`}>{item.value}</span>
      <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-300" />
    </>
  );
  const className = 'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50';

  return item.to ? (
    <Link to={item.to} className={className}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={item.onClick} className={className}>
      {body}
    </button>
  );
};

export default Dashboard;
